import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import Typography from '../components/Typography';
import Button from '../components/Button/Button';
import CloseIcon from '../components/Icons/CloseIcon';
import ArrowLeftIcon from '../components/Icons/ArrowLeftIcon';

/**
 * Página de error para el flujo OAuth de Stripe
 * Se muestra cuando hay un error al conectar una cuenta existente de Stripe
 */
const StripeOAuthError = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Obtener el mensaje de error de los parámetros de la URL
  const errorMessage = searchParams.get('error') || 'Error desconocido';
  const errorDescription = searchParams.get('error_description') || 'Ocurrió un error inesperado al conectar su cuenta de Stripe.';

  useEffect(() => {
    // Auto-redirigir después de 10 segundos
    const timer = setTimeout(() => {
      navigate('/dashboard');
    }, 10000);

    return () => clearTimeout(timer);
  }, [navigate]);

  const handleGoBack = () => {
    navigate('/dashboard');
  };

  const handleTryAgain = () => {
    // Redirigir al dashboard donde pueden intentar conectar nuevamente
    navigate('/dashboard');
  };

  return (
    <Container>
      <Card>
        <IconContainer>
          <CloseIcon width={64} height={64} fill="#EF4444" />
        </IconContainer>

        <Content>
          <Typography as="h1" $variant="title-2-semibold" style={{ textAlign: 'center' }}>
            Error al conectar cuenta de Stripe
          </Typography>

          <Typography as="p" $variant="body-1-medium" style={{ textAlign: 'center', color: '#6B7280' }}>
            No pudimos conectar su cuenta existente de Stripe en este momento.
          </Typography>

          <ErrorDetails>
            <Typography as="p" $variant="body-2-semibold" style={{ color: '#EF4444' }}>
              Error: {errorMessage}
            </Typography>
            <Typography as="p" $variant="body-3-medium" style={{ color: '#9CA3AF' }}>
              {errorDescription}
            </Typography>
          </ErrorDetails>

          <SolutionBox>
            <Typography as="p" $variant="body-2-semibold" style={{ marginBottom: '8px' }}>
              Posibles soluciones:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>
                <Typography as="span" $variant="body-3-medium" style={{ color: '#6B7280' }}>
                  Verifique que tiene una cuenta de Stripe válida
                </Typography>
              </li>
              <li>
                <Typography as="span" $variant="body-3-medium" style={{ color: '#6B7280' }}>
                  Asegúrese de autorizar el acceso cuando Stripe lo solicite
                </Typography>
              </li>
              <li>
                <Typography as="span" $variant="body-3-medium" style={{ color: '#6B7280' }}>
                  Intente crear una nueva cuenta de Stripe si no tiene una
                </Typography>
              </li>
              <li>
                <Typography as="span" $variant="body-3-medium" style={{ color: '#6B7280' }}>
                  Contacte al soporte si el problema persiste
                </Typography>
              </li>
            </ul>
          </SolutionBox>

          <Typography as="p" $variant="body-2-medium" style={{ textAlign: 'center', color: '#9CA3AF' }}>
            Será redirigido automáticamente en unos segundos...
          </Typography>
        </Content>

        <ButtonContainer>
          <Button onClick={handleTryAgain} $variant="primary">
            Intentar nuevamente
          </Button>
          <Button onClick={handleGoBack} $variant="outline">
            <ArrowLeftIcon width={16} height={16} />
            Volver al Dashboard
          </Button>
        </ButtonContainer>
      </Card>
    </Container>
  );
};

export default StripeOAuthError;

// Styled Components
const Container = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.fill.defaultSubtle};
  display: flex;
  justify-content: center;
  min-height: 100vh;
  padding: ${({ theme }) => theme.sizing.md};
`;

const Card = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.fill.defaultMain};
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  border-radius: ${({ theme }) => theme.radius.lg};
  box-shadow: 0 10px 25px rgb(0 0 0 / 10%);
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.lg};
  max-width: 600px;
  padding: ${({ theme }) => theme.sizing.xl};
  width: 100%;
`;

const IconContainer = styled.div`
  align-items: center;
  background: #FEF2F2;
  border-radius: 50%;
  display: flex;
  height: 80px;
  justify-content: center;
  width: 80px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.md};
  text-align: center;
  width: 100%;
`;

const ErrorDetails = styled.div`
  background: #FEF2F2;
  border: 1px solid #FECACA;
  border-radius: ${({ theme }) => theme.radius.md};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
  padding: ${({ theme }) => theme.sizing.md};
`;

const SolutionBox = styled.div`
  background: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.sizing.md};
  text-align: left;
  
  ul {
    li {
      margin-bottom: 4px;
      
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
  width: 100%;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: row;
    justify-content: center;
  }
`;